import React from 'react';
import PropTypes from 'prop-types';
import Autosuggest from 'react-autosuggest';
import { omit, debounce } from 'lodash';
import gql from 'graphql-tag';
import styled from 'styled-components';
import { useStoreState } from 'easy-peasy';

import Input from '../elements/Input';
import client from '../../utils/apolloClient';

const companiesQuery = gql`
  query searchCompanies($input: SearchInput) {
    search_companies(input: $input) {
      total_size
      company_listing {
        company_id
        name
        city
        status
      }
    }
  }
`;

const SuggestionsContainer = styled.div`
  max-height: 20rem;
  overflow-y: scroll;
`;

const renderInputComponent = inputProps => {
  const props = omit(inputProps, ['className', 'ref']);
  return <Input {...props} />;
};

const renderSuggestion = suggestion => (
  <p className="is-size-4 has-text-weight-medium">
    {[suggestion.name, suggestion.city, suggestion.status]
      .filter(Boolean)
      .join(' - ')}
  </p>
);

const renderSuggestionsContainer = ({ containerProps, children }) =>
  children && (
    <SuggestionsContainer {...containerProps}>{children}</SuggestionsContainer>
  );

const CompanySuggestions = ({
  label,
  initialValue,
  onSelect,
  onHandleClear,
  placeholder,
}) => {
  const { userId } = useStoreState(state => state.auth);
  const [value, setValue] = React.useState(`${initialValue}`);
  const [suggestions, setSuggestions] = React.useState([]);

  const onChange = (event, { newValue }) => {
    setValue(newValue);
    if (!newValue) {
      onHandleClear(newValue);
    }
  };

  const getSuggestionValue = suggestion => {
    onSelect(suggestion);
    return suggestion.name;
  };

  const onSuggestionsFetchRequested = inputValue => {
    const input = { user_id: userId };
    const company_filter = {};
    Object.assign(
      company_filter,
      inputValue.value && { name: inputValue.value.toLowerCase() },
    );
    Object.assign(input, { filter: { company_filter } });

    client.clientPrivate
      .query({
        query: companiesQuery,
        variables: { input },
        fetchPolicy: 'network-only',
      })
      .then(({ data }) => {
        if (
          data?.search_companies?.company_listing &&
          Array.isArray(data.search_companies.company_listing)
        ) {
          setSuggestions(data.search_companies.company_listing);
        } else {
          setSuggestions([]);
        }
      })
      .catch(() => {
        setSuggestions([]);
      });
  };

  const debouncedFetch = React.useRef(
    debounce((func, input) => func(input), 300),
  );

  const fetchData = inputValue => {
    debouncedFetch.current(onSuggestionsFetchRequested, inputValue);
  };

  const onSuggestionsClearRequested = () => {
    setSuggestions([]);
  };

  const inputProps = {
    placeholder: placeholder || 'Search Company',
    value,
    onChange,
    type: 'Search',
  };

  return (
    <div className="field">
      {label && <label className="label">{label}</label>}
      <div className="control">
        <Autosuggest
          suggestions={suggestions}
          onSuggestionsFetchRequested={fetchData}
          onSuggestionsClearRequested={onSuggestionsClearRequested}
          getSuggestionValue={getSuggestionValue}
          renderSuggestion={renderSuggestion}
          renderInputComponent={renderInputComponent}
          renderSuggestionsContainer={renderSuggestionsContainer}
          inputProps={inputProps}
          focusInputOnSuggestionClick={false}
        />
      </div>
    </div>
  );
};

CompanySuggestions.defaultProps = {
  label: '',
  initialValue: '',
  onSelect: () => {},
  onHandleClear: () => {},
  placeholder: '',
};

CompanySuggestions.propTypes = {
  label: PropTypes.string,
  initialValue: PropTypes.string,
  onSelect: PropTypes.func,
  onHandleClear: PropTypes.func,
  placeholder: PropTypes.string,
};

export default CompanySuggestions;
